import { useState } from "react";
import { Share2, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Native Web Share API button with a copy-link fallback.
 *
 * - On phones/tablets (and Safari): uses navigator.share() to open the
 *   system share sheet so visitors can ping the article straight into
 *   WhatsApp, Messages, Twitter, etc.
 * - On desktop browsers without navigator.share: silently falls back
 *   to copying the link to the clipboard.
 *
 * Props:
 *   title       – Title to share (becomes the share-sheet headline)
 *   text        – Optional descriptive text
 *   url         – Absolute or relative URL; defaults to current page
 *   articleSlug – Optional: when present, fires a tracking beacon to
 *                 POST /api/articles/{slug}/share so the admin can see
 *                 most-shared stories of the week.
 *   label       – Button label (defaults to "SHARE")
 *   testId      – Optional data-testid
 */
export default function ShareButton({ title, text, url, articleSlug, label = "SHARE", testId = "share-button" }) {
    const [copied, setCopied] = useState(false);
    const resolvedUrl =
        url || (typeof window !== "undefined" ? window.location.href : "");

    const fireBeacon = (method) => {
        if (!articleSlug) return;
        // Fire-and-forget — never block the share UX on a tracking call.
        api.post(`/articles/${articleSlug}/share`, { method }).catch(() => {});
    };

    const copyFallback = async () => {
        try {
            await navigator.clipboard.writeText(resolvedUrl);
            setCopied(true);
            toast.success("Link copied — paste it anywhere");
            setTimeout(() => setCopied(false), 2000);
            fireBeacon("copy");
        } catch {
            toast.error("Could not copy link");
        }
    };

    const onShare = async () => {
        if (typeof navigator !== "undefined" && navigator.share) {
            try {
                await navigator.share({ title, text, url: resolvedUrl });
                fireBeacon("native");
            } catch (e) {
                // User cancelled or share unsupported → silent fallback
                if (e?.name !== "AbortError") copyFallback();
            }
        } else {
            copyFallback();
        }
    };

    return (
        <div className="inline-flex items-center gap-2">
            <button
                type="button"
                onClick={onShare}
                data-testid={testId}
                aria-label="Share this story"
                className="inline-flex items-center gap-2 bg-streetz-red text-white overline px-4 py-2 hover:bg-white hover:text-black transition-colors"
            >
                <Share2 className="w-4 h-4" /> {label}
            </button>
            <button
                type="button"
                onClick={copyFallback}
                data-testid={`${testId}-copy`}
                aria-label="Copy link"
                title="Copy link"
                className="inline-flex items-center gap-2 overline px-3 py-2 border border-streetz-line text-white hover:border-streetz-yellow hover:text-streetz-yellow transition-colors"
            >
                {copied ? <Check className="w-4 h-4 text-streetz-yellow" /> : <LinkIcon className="w-4 h-4" />}
                <span className="hidden sm:inline">{copied ? "COPIED" : "COPY LINK"}</span>
            </button>
        </div>
    );
}
